import {
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    Link as MUILink,
    Menu,
    MenuItem,
    Snackbar,
    Toolbar,
    Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import clsx from 'clsx';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink as Link } from 'react-router-dom';

import { ReactComponent as BugIcon } from '../../../assets/icons/bug.svg';
import { ReactComponent as Discussion } from '../../../assets/icons/discussion.svg';
import { ReactComponent as DiscussionNotification } from '../../../assets/icons/discussion-notification.svg';
import { ReactComponent as FeedbackIcon } from '../../../assets/icons/feedback.svg';
import { ConversationType } from '../../../enums/chat';
import { useAppDispatch } from '../../../hooks';
import ConversationModal from '../../../pages/Chat/components/ConversationModal';
import DiscussionsModal from '../../../pages/Chat/components/DiscussionsModal';
import useIntervalCallback from '../../../pages/Chat/hooks/useIntervalCallback';
import { ChatSelectors, closeChat, fetchUserPermissions, openChatAtMessage, useConversations } from '../../../reducers/chat';
import { clearFeedbackError, clearFeedbackSuccess, clearReportBugError, clearReportBugSuccess, GeneralSelectors, sendBugReport, sendGlobalFeedback } from '../../../reducers/general';
import { HistorySelectors, loadWorkHistory } from '../../../reducers/history';
import { WorkSelectors } from '../../../reducers/work';
import { humanize } from '../../../services/utils';
import { colors } from '../../../theme';
import { CONVERSATION_PAGE_SIZE, FETCH_CONVERSATION_INTERVAL } from '../../../types/chat';
import { FeedbackType } from '../../../types/general';
import { parseEntityId } from '../../../utils/chat';
import FeedbackBugDialog from '../../feedbackBugDialog';
import Logo from './logo';

const useStyles = makeStyles(theme => {
    return {
        toolbar: {
            display: 'flex',
            justifyContent: 'space-between',
            background: theme.palette.primary.dark
        },
        toolbarSide: {
            flex: 1
        },
        toolbarEnd: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'end'
        },
        navBarButton: {
            color: theme.palette.text.primary,
            margin: '0px 10px 0px 0px',
            '&:disabled': {
                color: theme.palette.action.disabled
            }
        },
        bugIcon: {
            width: '20px',
            height: '20px'
        },
        helpButton: {
            margin: '0px 10px 0px 0px'
        },
        helpIcon: {
            color: theme.palette.text.primary,
            borderRadius: '50%'
        },
        menuItem: {
            color: theme.palette.text.secondary
        },
        historyButton: {
            color: 'white',
            backgroundColor: colors.chineseGold,
            '&:hover': {
                backgroundColor: colors.chineseGold,
                color: colors.black87
            },
            borderRadius: 30
        },
        discussionContainer: {
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 16px 0 0',
            backgroundColor: 'rgba(245, 179, 9, 0.1)',
            borderRadius: '50%',
            width: '46px',
            height: '46px',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: 'rgba(245, 179, 9, 0.2)'
            }
        },
        discussionIcon: {
            width: '20px',
            height: '20px',
            color: theme.palette.text.primary
        },
        discussionNotification: {
            position: 'absolute',
            top: '8px',
            right: '9px',
            width: '10px',
            height: '10px',
            color: theme.palette.error.main
        }
    };
});

type Props = {
    signedIn: boolean;
    onSignOut: () => void;
    isAdmin: boolean;
};

const NavBar: FunctionComponent<Props> = ({
    signedIn,
    onSignOut,
    isAdmin
}: Props) => {
    const classes = useStyles();

    const [ anchorEl, setAnchorEl ] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [ discussionsModalOpen, setDiscussionsModalOpen ] = useState(false);
    const [ feedbackDialogType, setFeedbackDialogType ] = useState<FeedbackType | null>(null);
    const [ dialogDetails, setDialogDetails ] = useState('');
    const dispatch = useAppDispatch();

    const { work } = WorkSelectors();
    const { isReportBugLoading, isReportBugError, isReportBugSuccess, isFeedbackLoading, isFeedbackError, isFeedbackSuccess } = GeneralSelectors();
    const { unreadBlameIssuesCount, completedWork } = HistorySelectors();

    useEffect(() => {
        if (!signedIn) {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        const conversationId = params.get('conversationId');
        const entityId = params.get('entityId');
        const messageId = params.get('messageId');

        if (conversationId && entityId && messageId) {
            const id = Number(conversationId);
            const msgId = Number(messageId);
            dispatch(openChatAtMessage({
                conversationId: id,
                entityId: decodeURIComponent(entityId),
                messageId: msgId,
                resultsPerPage: CONVERSATION_PAGE_SIZE
            }));
            params.delete('conversationId');
            params.delete('entityId');
            params.delete('messageId');
            const newSearch = params.toString();
            window.history.replaceState(
                {},
                '',
                newSearch ? `?${newSearch}` : window.location.pathname
            );
        }
    }, [ signedIn, dispatch ]);

    useEffect(() => {
        if (signedIn && !completedWork) {
            dispatch(loadWorkHistory({}));
        }
    }, [ signedIn, completedWork, dispatch ]);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
  
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCloseDiscussions = () => {
        setDiscussionsModalOpen(false);
    };

    const handleOpenDiscussions = () => {
        setDiscussionsModalOpen(true);
    };

    const handleReportBug = () => {
        setFeedbackDialogType(FeedbackType.BUG);
    };

    const handleFeedback = () => {
        setFeedbackDialogType(FeedbackType.FEEDBACK);
    };

    const handleDialogClose = () => {
        setFeedbackDialogType(null);
        setDialogDetails('');
    };

    const handleDialogSubmit = (attachmentIds: number[]) => {
        if (feedbackDialogType === FeedbackType.BUG) {
            const payload = {
                details: dialogDetails,
                attachmentList: attachmentIds.length > 0 ? attachmentIds : undefined,
                workId: work ? work.id : undefined
            };
            dispatch(sendBugReport(payload))
                .unwrap().then(() => {
                    setFeedbackDialogType(null);
                    setDialogDetails('');
                }).catch((error) => {
                    console.error('Error submitting bug report:', error);
                    setFeedbackDialogType(null);
                    setDialogDetails('');
                });
        } else if (feedbackDialogType === FeedbackType.FEEDBACK) {
            dispatch(
                sendGlobalFeedback({
                    details: dialogDetails,
                    attachmentList: attachmentIds.length > 0 ? attachmentIds : undefined
                })
            ).unwrap().then(() => {
                setFeedbackDialogType(null);
                setDialogDetails('');
            }).catch((error) => {
                console.error('Error submitting feedback:', error);
            });
        }
    };
    
    const handleCloseChatModal = useCallback(() => {
        dispatch(closeChat());
    }, [ dispatch ]);

    const conversations = useConversations();
    const chatState = ChatSelectors();
    const {
        isOpen: isChatOpen,
        activeConversationId,
        entityId: activeConversationEntityId,
        conversationName: activeConversationName
    } = chatState;

    const activeConversation =
        activeConversationId !== null
            ? conversations[activeConversationId]
            : undefined;

    const activeEntityId =
        activeConversation?.entityId ?? activeConversationEntityId ?? '';
    const activeConversationTitle =
        humanize(activeConversation?.conversationName || '') ??
        humanize(activeConversationName) ??
        'Conversation';
    const activeConversationSubtitle =
        humanize(activeConversation?.projectName || '') ??
        humanize(activeConversation?.entityId || '') ??
        null;
    const parsedEntity = parseEntityId(activeEntityId);
    const activeConversationType =
        parsedEntity.conversationType ?? ConversationType.PROJECT;
    const isChatModalVisible =
        Boolean(activeConversationId) && Boolean(isChatOpen);

    const pollUserPermissions = useCallback(() => {
        if (!signedIn) {
            return Promise.resolve(undefined);
        }
        return dispatch(fetchUserPermissions())
            .unwrap()
            .catch(() => undefined);
    }, [ dispatch ]);

    useEffect(() => {
        pollUserPermissions();
    }, [ pollUserPermissions ]);

    useIntervalCallback(pollUserPermissions, FETCH_CONVERSATION_INTERVAL);

    const hasUnreadConversations = useMemo(() => {
        return Object.values(conversations).some(
            (conversation) => conversation.unreadMessageCount > 0
        );
    }, [ conversations ]);

    return (
        <Toolbar className={classes.toolbar}>
            <DiscussionsModal
                open={discussionsModalOpen}
                onClose={handleCloseDiscussions}
            />
            <FeedbackBugDialog
                open={feedbackDialogType !== null}
                onClose={handleDialogClose}
                details={dialogDetails}
                onDetailsChange={setDialogDetails}
                onSubmit={handleDialogSubmit}
                isLoading={feedbackDialogType === FeedbackType.BUG ? isReportBugLoading : isFeedbackLoading}
                title={feedbackDialogType === FeedbackType.BUG ? 'Report a Bug' : 'Feedback'}
                description={
                    feedbackDialogType === FeedbackType.BUG
                        ? 'The bug report feature helps our tech team resolve beehive development issues (components off, flow stuck, etc). Report web app issues here; for extension bugs, please report them via the extension.'
                        : 'Your feedback helps us improve Beehive. Please let us know if you have any suggestions or comments.'
                }
                placeholder={
                    feedbackDialogType === FeedbackType.BUG
                        ? 'Please describe the bug...'
                        : 'Please describe your feedback...'
                }
                ariaLabelId={feedbackDialogType === FeedbackType.BUG ? 'bug-dialog-title' : 'feedback-dialog-title'}
            />
            <ConversationModal
                open={isChatModalVisible}
                onClose={handleCloseChatModal}
                conversationId={activeConversationId ?? 0}
                conversationType={activeConversationType}
                entityId={activeEntityId}
                title={activeConversationTitle}
                subtitle={activeConversationSubtitle ?? undefined}
            />

            <Box className={classes.toolbarSide}>
                <Link to="/">
                    <Logo test-id="logo-image" />
                </Link>
            </Box>
            {signedIn && 
            <Box>
                <Link to="/history">
                    <Badge color="secondary" badgeContent={unreadBlameIssuesCount} invisible={unreadBlameIssuesCount === 0}>
                        <Button
                            className={classes.historyButton}
                            variant="contained"
                            type="submit"
                            disabled={!signedIn}
                        >
                            History
                        </Button>
                    </Badge>
                </Link>
            </Box>
            }
            {signedIn && (
                <Box className={clsx(classes.toolbarSide, classes.toolbarEnd)}>
                    <Box 
                        className={classes.discussionContainer}
                        onClick={handleOpenDiscussions}
                    >
                        <Discussion className={classes.discussionIcon} />
                        {hasUnreadConversations && (
                            <DiscussionNotification className={classes.discussionNotification} />
                        )}
                    </Box>
                    <Tooltip
                        title="Report bug"
                    >
                        <span>
                            <IconButton
                                onClick={handleReportBug}
                                classes={{
                                    root: classes.navBarButton
                                }}
                                aria-label="report-bug"
                            >
                                <BugIcon className={classes.bugIcon} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title="Send feedback"
                    >
                        <span>
                            <IconButton
                                onClick={handleFeedback}
                                classes={{
                                    root: classes.navBarButton
                                }}
                                aria-label="send-feedback"
                            >
                                <FeedbackIcon className={classes.bugIcon} />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Link to="#">
                        <IconButton
                            aria-label="my-account"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                            classes={{
                                root: classes.navBarButton
                            }}
                        >
                            <AccountCircleIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            getContentAnchorEl={null}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right'
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                            open={open}
                            onClose={handleClose}
                        >
                            <MenuItem
                                className={classes.menuItem}
                                component={Link}
                                to="/profile"
                            >
                                Settings
                            </MenuItem>
                            {isAdmin && (
                                <MenuItem
                                    className={classes.menuItem}
                                    component={Link}
                                    to="/dashboard"
                                >
                                    Admin Dashboard
                                </MenuItem>
                            )}
                            <MenuItem
                                className={classes.menuItem}
                                component={Link}
                                to="/history"
                            >
                                History
                            </MenuItem>
                            <MenuItem
                                className={classes.menuItem}
                                component={MUILink}
                                href="https://docs.caas.ai/community"
                                target="_blank" 
                                rel="noopener noreferrer"
                            >
                                Help
                            </MenuItem>
                            <Divider />
                            <MenuItem
                                className={classes.menuItem}
                                onClick={onSignOut}
                            >
                                Sign out
                            </MenuItem>
                        </Menu>
                    </Link>
                </Box>
            )}
            <Snackbar
                open={isReportBugError}
                autoHideDuration={4000}
                onClose={() => dispatch(clearReportBugError())}
                message="Failed to submit bug report. Please try again."
            />
            <Snackbar
                open={isReportBugSuccess}
                autoHideDuration={4000}
                onClose={() => dispatch(clearReportBugSuccess())}
                message="Bug report submitted successfully. Thank you!"
            />
            <Snackbar
                open={isFeedbackError}
                autoHideDuration={4000}
                onClose={() => dispatch(clearFeedbackError())}
                message="Failed to submit feedback. Please try again."
            />
            <Snackbar
                open={isFeedbackSuccess}
                autoHideDuration={4000}
                onClose={() => dispatch(clearFeedbackSuccess())}
                message="Feedback submitted successfully. Thank you!"
            />
        </Toolbar>
    );
};

export default NavBar;
